import React, { FC } from "react";
import { AiOutlineClose } from "react-icons/ai";
import useOutsideClick from "hooks/click-outside";
import { useForm } from "react-hook-form";
import { FaFacebookF, FaInstagram, FaX } from "react-icons/fa6";
import clsx from "clsx";
import useAmbassador from "containers/ambassador/useHooks";

interface BusinessRegistrationLinkProps {
  isOpen: boolean;
  onClose: () => void;
  handleConfirm?: () => void;
}

const BusinessRegistrationLink: FC<BusinessRegistrationLinkProps> = ({
  isOpen,
  onClose,
  handleConfirm,
}) => {
  const modalRef = useOutsideClick(() => {
    if (isOpen) onClose();
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ link: string }>({
    defaultValues: { link: "" },
  });
  const { postCreateShare } = useAmbassador();

  const link = watch("link");

  const platform = (() => {
    if (link.includes("facebook.com")) return "facebook";
    if (link.includes("x.com") || link.includes("twitter.com")) return "x";
    if (link.includes("instagram.com")) return "instagram";
    return null;
  })();

  const onSubmit = (data: { link: string }) => {
    if (handleConfirm) handleConfirm();
    const body = { socialMediaLink: data.link };
    postCreateShare(body);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
    >
      <div
        ref={modalRef}
        className={`relative bg-white px-4 sm:px-6 py-6 sm:py-8 rounded-lg shadow-lg w-[90%] sm:w-[500px] md:w-[550px] transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition"
        >
          <AiOutlineClose size={20} />
        </button>

        <h2 className="text-xl sm:text-2xl font-bold mb-2 text-rsvpBlackie">
          Social Media Activity
        </h2>
        <p className="text-left text-sm sm:text-base text-rsvpBlackie mb-6">
          Spread the Learn Local message with Social Media Activity
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-[#F8F8F8] px-4 py-3 rounded-xl mb-1">
            <input
              type="text"
              {...register("link", {
                required: "Link is required",
                validate: (value) => {
                  const trimmed = value.trim();
                  if (trimmed === "") return "Link cannot be empty";
                  const isValidSocialMedia =
                    trimmed.includes("facebook.com") ||
                    trimmed.includes("instagram.com") ||
                    trimmed.includes("twitter.com") ||
                    trimmed.includes("x.com");

                  if (!isValidSocialMedia) {
                    return "Please enter a valid Facebook, Instagram, or X (Twitter) link";
                  }

                  // Check if it starts with http or https
                  if (
                    !trimmed.startsWith("http://") &&
                    !trimmed.startsWith("https://")
                  ) {
                    return "Please include http:// or https:// in your link";
                  }

                  return true;
                },
              })}
              className="bg-[#F8F8F8] outline-none w-full"
              placeholder="Enter social media link"
            />
          </div>
          {errors.link && (
            <p className="text-sm text-red-500 mb-3">{errors.link.message}</p>
          )}

          <div className="text-left mb-6">
            <div className="flex justify-left gap-4 items-center">
              <p className="text-sm text-rsvpBlackie">Share via</p>

              <FaFacebookF
                className={clsx("w-5 h-5 cursor-pointer transition-transform", {
                  "text-blue-600 scale-110": platform === "facebook",
                  "text-gray-400": platform && platform !== "facebook",
                  "text-blue-600": !platform,
                })}
              />

              <FaX
                className={clsx("w-5 h-5 cursor-pointer transition-transform", {
                  "text-black scale-110": platform === "x",
                  "text-gray-400": platform && platform !== "x",
                  "text-black": !platform,
                })}
              />

              <FaInstagram
                className={clsx("w-5 h-5 cursor-pointer transition-transform", {
                  "text-pink-500 scale-110": platform === "instagram",
                  "text-gray-400": platform && platform !== "instagram",
                  "text-pink-500": !platform,
                })}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <button
              type="submit"
              className="flex-1 bg-ambassadorBlue text-white py-3 rounded-full font-medium text-sm sm:text-base"
            >
              Post Link
            </button>
            <button
              type="button"
              className="flex-1 bg-[#F1F1F1] text-rsvpBlackie py-3 rounded-full font-medium text-sm sm:text-base"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessRegistrationLink;
